// frontend/src/features/workspace/api/use-models.ts
import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import { envelopePayload } from '@/lib/api-envelope'
import type { Capability } from '@shared/collaboration/access'

export interface ModelSummary {
  id: string
  projectId: string
  tenantId: string
  createdBy: string
  name: string
  sourceType: string
  importJobId: string | null
  pipeCount: number
  calcStatus: string
  calcSummary: { iterations?: number; warningsCount?: number } | null
  version: number
  createdAt: string
  updatedAt: string
  capabilities?: Capability[] | undefined
}

export interface ModelFull extends ModelSummary {
  system: unknown
}

export interface CreateModelParams {
  projectId: string
  name: string
  sourceType: string
  system: unknown
  importJobId?: string
}

export interface UpdateModelParams {
  projectId: string
  modelId: string
  system: unknown
  version: number
  calcStatus?: string
  calcSummary?: { iterations?: number; warningsCount?: number }
  pipeCount?: number
}

export function useModels(projectId: string | undefined): UseQueryResult<ModelSummary[]> {
  const api = useApi()

  return useQuery<ModelSummary[]>({
    queryKey: ['models', projectId],
    queryFn: (): Promise<Array<ModelSummary>> => api.get<ModelSummary[]>(`/projects/${projectId}/models`),
    enabled: !!projectId,
  })
}

export function useModel(projectId: string | undefined, modelId: string | undefined): UseQueryResult<ModelFull> {
  const api = useApi()

  return useQuery<ModelFull>({
    queryKey: ['models', projectId, modelId],
    queryFn: (): Promise<ModelFull> => api.get<ModelFull>(`/projects/${projectId}/models/${modelId}`),
    enabled: !!projectId && !!modelId,
  })
}

export function useCreateModel(): UseMutationResult<{ id: string; version: number }, Error, CreateModelParams> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<{ id: string; version: number }, Error, CreateModelParams>({
    mutationFn: ({ projectId, ...params }): Promise<{ id: string; version: number; }> =>
      api.post<{ id: string; version: number }>(`/projects/${projectId}/models`, params),
    onSuccess: (_, variables): void => {
      queryClient.invalidateQueries({ queryKey: ['models', variables.projectId] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // Project list shows modelCount per project; bump it after a new
      // model lands so the Projects page stays in sync without a refresh.
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useUpdateModel(): UseMutationResult<{ version: number }, Error & { status?: number; currentVersion?: number; currentSystem?: unknown }, UpdateModelParams> {
  const api = useApi()

  return useMutation<
    { version: number },
    Error & { status?: number; currentVersion?: number; currentSystem?: unknown },
    UpdateModelParams
  >({
    mutationFn: async ({ projectId, modelId, ...params }): Promise<{ version: number; }> => {
      // Use api.put() instead of raw fetch() to ensure the auth token is included.
      // The api client throws on non-2xx, so we catch and inspect the error for 409.
      try {
        return await api.put<{ version: number }>(
          `/projects/${projectId}/models/${modelId}`,
          params,
        )
      } catch (error: unknown) {
        // Conflict errors thrown by the api client carry status + body fields via
        // either a top-level `.status` or a nested `.response.status` (axios-style).
        // Narrow through an indexed shape rather than `any` so the access stays typed.
        const e = error as {
          status?: number
          response?: { status?: number }
          data?: { currentVersion?: number; currentSystem?: unknown }
          body?: { currentVersion?: number; currentSystem?: unknown }
        }
        // ⚠ UNREACHABLE TODAY, and was before the envelope work — this is not a
        // regression, but it is not a working recovery path either.
        //
        // `useApi` is `@revalize/api-client`'s ApiClient, which on any non-2xx
        // throws `new Error(\`HTTP ${status}: ${statusText}\`)` without parsing
        // the body or attaching `status`/`body` (ApiClient.ts:96-105). Neither
        // condition below can be true, so the caller gets a generic HTTP error
        // rather than currentVersion/currentSystem, and last-write-wins
        // reconciliation never runs.
        //
        // Reaching it means moving this save onto `apiFetch`, which does attach
        // both. Kept out of the envelope PR deliberately: changing the
        // transport of a save path deserves its own blast-radius review.
        if (e?.status === 409 || e?.response?.status === 409) {
          // A 409 body arrives under `error`, not `data` — see api-envelope.ts.
          const conflict = (envelopePayload(e.data ?? e.body) ?? {}) as {
            currentVersion?: number
            currentSystem?: unknown
          }
          // currentVersion may legitimately be undefined when the server omits the field;
          // the outer mutation signature already marks it optional, so we preserve that.
          const err = new Error('Version conflict') as Error & {
            status: number
            currentVersion: number | undefined
            currentSystem: unknown
          }
          err.status = 409
          err.currentVersion = conflict.currentVersion
          err.currentSystem = conflict.currentSystem
          throw err
        }
        throw error
      }
    },
    // No automatic cache invalidation — auto-save is frequent, we don't want to spam refetches
  })
}

export function useDeleteModel(): UseMutationResult<void, Error, { projectId: string; modelId: string }> {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { projectId: string; modelId: string }>({
    mutationFn: ({ projectId, modelId }): Promise<void> =>
      api.delete(`/projects/${projectId}/models/${modelId}`),
    onSuccess: (_, variables): void => {
      queryClient.invalidateQueries({ queryKey: ['models', variables.projectId] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // Project list shows modelCount per project; drop it after a
      // delete so the Projects page stays in sync without a refresh.
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}
